import React, { useEffect, useRef, useState } from 'react'
import { Play, Pause, RotateCcw } from 'lucide-react'
import { AlignmentSegment } from '../utils/audioAnalysis'

// Declare Multitrack global type
declare global {
  interface Window {
    Multitrack: any
  }
}

interface AlignedWaveformPlayerProps {
  experimentAudioUrl: string | null
  testAudioUrl: string | null
  experimentTitle?: string
  testTitle?: string
  alignmentSegments: AlignmentSegment[]
  matchStartTime?: number
  matchEndTime?: number
  onExperimentBufferLoaded?: (buffer: AudioBuffer) => void
  onTestBufferLoaded?: (buffer: AudioBuffer) => void
}

export const AlignedWaveformPlayer: React.FC<AlignedWaveformPlayerProps> = ({
  experimentAudioUrl,
  testAudioUrl,
  experimentTitle = '实验音频',
  testTitle = '测试音频',
  alignmentSegments,
  matchStartTime,
  matchEndTime,
  onExperimentBufferLoaded,
  onTestBufferLoaded
}) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const multitrackRef = useRef<any>(null)
  const audioContextRef = useRef<AudioContext | null>(null)
  const animationFrameRef = useRef<number | null>(null)
  
  const [isPlaying, setIsPlaying] = useState(false)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)
  const [isLoading, setIsLoading] = useState(false)
  const [canPlay, setCanPlay] = useState(false)
  const [scriptLoaded, setScriptLoaded] = useState(false)
  const [muteState, setMuteState] = useState<'both' | 'track0' | 'track1'>('both')
  const [timeOffset, setTimeOffset] = useState(0)
  const [track0Duration, setTrack0Duration] = useState(0)
  const [track1Duration, setTrack1Duration] = useState(0)

  // Load multitrack script
  useEffect(() => {
    if (window.Multitrack) {
      setScriptLoaded(true)
      return
    }

    const script = document.createElement('script')
    script.src = '/multitrack.min.js'
    script.onload = () => {
      console.log('Multitrack script loaded')
      setScriptLoaded(true)
    }
    script.onerror = () => {
      console.error('Failed to load Multitrack script')
      setScriptLoaded(false)
    }
    document.head.appendChild(script)

    return () => {
      if (document.head.contains(script)) {
        document.head.removeChild(script)
      }
    }
  }, [])

  // Initialize audio context for buffer loading
  useEffect(() => {
    audioContextRef.current = new (window.AudioContext || (window as any).webkitAudioContext)()
    return () => {
      if (audioContextRef.current) {
        audioContextRef.current.close()
      }
    }
  }, [])

  // Calculate time offset from alignment segments
  useEffect(() => {
    if (alignmentSegments && alignmentSegments.length > 0) {
      // 使用第一个对齐点的偏移
      const firstOffset = alignmentSegments[0].offset
      setTimeOffset(firstOffset)
      console.log('Applied time offset from first alignment point:', firstOffset)
    } else {
      setTimeOffset(0)
    }
  }, [alignmentSegments])

  // Load audio buffers for DTW analysis
  useEffect(() => {
    if (!experimentAudioUrl || !audioContextRef.current) return

    const loadAudio = async () => {
      try {
        const response = await fetch(experimentAudioUrl)
        const arrayBuffer = await response.arrayBuffer()
        const audioBuffer = await audioContextRef.current!.decodeAudioData(arrayBuffer)
        setTrack0Duration(audioBuffer.duration)
        onExperimentBufferLoaded?.(audioBuffer)
      } catch (error) {
        console.error('Error loading experiment audio buffer:', error)
      }
    }

    loadAudio()
  }, [experimentAudioUrl, onExperimentBufferLoaded])

  useEffect(() => {
    if (!testAudioUrl || !audioContextRef.current) return

    const loadAudio = async () => {
      try {
        const response = await fetch(testAudioUrl)
        const arrayBuffer = await response.arrayBuffer()
        const audioBuffer = await audioContextRef.current!.decodeAudioData(arrayBuffer)
        setTrack1Duration(audioBuffer.duration)
        onTestBufferLoaded?.(audioBuffer)
      } catch (error) {
        console.error('Error loading test audio buffer:', error)
      }
    }

    loadAudio()
  }, [testAudioUrl, onTestBufferLoaded])

  // Initialize multitrack player
  useEffect(() => {
    if (!containerRef.current || !scriptLoaded || !window.Multitrack) {
      return
    }

    if (!experimentAudioUrl || !testAudioUrl) {
      return
    }

    setIsLoading(true)
    setCanPlay(false)
    setIsPlaying(false)

    // Destroy previous instance
    if (multitrackRef.current) {
      console.log('Destroying previous multitrack instance')
      multitrackRef.current.destroy()
    }

    try {
      // 如果偏移为正，说明测试音频超前，给实验音频添加延迟
      // 如果偏移为负，说明实验音频超前，给测试音频添加延迟
      const track0StartPosition = timeOffset > 0 ? timeOffset : 0
      const track1StartPosition = timeOffset < 0 ? Math.abs(timeOffset) : 0
      
      const tracks = [
        {
          id: 0,
          url: experimentAudioUrl,
          draggable: true,
          startPosition: track0StartPosition,
          options: {
            waveColor: 'rgb(147, 51, 234)', // purple - experiment
            progressColor: 'rgb(109, 40, 175)',
            height: 100,
          }
        },
        {
          id: 1,
          url: testAudioUrl,
          draggable: true,
          startPosition: track1StartPosition,
          options: {
            waveColor: 'rgb(59, 130, 246)', // blue - test
            progressColor: 'rgb(29, 78, 216)',
            height: 100,
          }
        }
      ]

      console.log('Creating multitrack with tracks:', tracks)
      console.log('Time offset:', timeOffset)
      console.log('Track 0 (Experiment) start position:', track0StartPosition)
      console.log('Track 1 (Test) start position:', track1StartPosition)
      multitrackRef.current = window.Multitrack.create(tracks, {
        container: containerRef.current,
        minPxPerSec: 10,
        rightButtonDrag: false,
        cursorWidth: 2,
        cursorColor: '#D72F21',
        trackBackground: '#f9fafb',
        trackBorderColor: '#e5e7eb',
        dragBounds: false,
      })

      // Set up event listeners
      multitrackRef.current.on('canplay', () => {
        console.log('Multitrack can play')
        setIsLoading(false)
        setCanPlay(true)
        setDuration(multitrackRef.current.getDuration())
      })

      multitrackRef.current.on('timeupdate', () => {
        setCurrentTime(multitrackRef.current.getCurrentTime())
      })

      multitrackRef.current.on('play', () => {
        setIsPlaying(true)
      })

      multitrackRef.current.on('pause', () => {
        setIsPlaying(false)
      })

      multitrackRef.current.on('finish', () => {
        setIsPlaying(false)
      })

      multitrackRef.current.on('error', (error: any) => {
        console.error('Multitrack error:', error)
        setIsLoading(false)
      })

    } catch (error) {
      console.error('Error creating multitrack:', error)
      setIsLoading(false)
    }

    return () => {
      if (multitrackRef.current) {
        console.log('Cleaning up multitrack instance')
        multitrackRef.current.destroy()
      }
    }
  }, [scriptLoaded, experimentAudioUrl, testAudioUrl, timeOffset])

  // Draw alignment lines on canvas overlay
  useEffect(() => {
    const canvas = canvasRef.current
    const container = containerRef.current
    if (!canvas || !container || !multitrackRef.current || !canPlay) return
    if (alignmentSegments.length === 0) return

    const drawAlignmentLines = () => {
      const ctx = canvas.getContext('2d')
      if (!ctx) return

      // 设置canvas尺寸匹配容器
      const rect = container.getBoundingClientRect()
      if (rect.width === 0 || rect.height === 0) return
      
      canvas.width = rect.width
      canvas.height = rect.height

      // 清空canvas
      ctx.clearRect(0, 0, canvas.width, canvas.height)

      // 获取每条轨道的尺寸和位置信息（基于wavesurfer-multitrack的实际布局）
      const trackHeight = 100 // 与multitrack options中的height一致
      const trackGap = 20 // 轨道之间的间隙
      const topPadding = 10 // 顶部padding
      const track0Y = topPadding // 第一条轨道的y位置
      const track1Y = track0Y + trackHeight + trackGap // 第二条轨道的y位置

      // 获取当前zoom级别（从multitrack获取实际的像素/秒比例）
      const duration = multitrackRef.current.getDuration()
      if (duration === 0) return
      
      // 简化计算：直接基于容器宽度和总时长
      const pxPerSec = canvas.width / duration

      // 绘制对齐连线（每隔几个点绘制一条，避免太密集）
      const step = Math.max(1, Math.floor(alignmentSegments.length / 20))
      
      for (let i = 0; i < alignmentSegments.length; i += step) {
        const segment = alignmentSegments[i]
        
        // 根据偏移方向计算位置
        let x1, x2
        if (timeOffset > 0) {
          // 偏移为正：实验音频被延迟，测试音频从0开始
          x1 = (segment.time1 + timeOffset) * pxPerSec  // 实验音频加上延迟
          x2 = segment.time2 * pxPerSec  // 测试音频从0开始
        } else {
          // 偏移为负：测试音频被延迟，实验音频从0开始
          x1 = segment.time1 * pxPerSec  // 实验音频从0开始
          x2 = (segment.time2 + Math.abs(timeOffset)) * pxPerSec  // 测试音频加上延迟
        }
        
        const y1 = track0Y + trackHeight
        const y2 = track1Y
        
        // 检查坐标是否在canvas范围内
        if (x1 < 0 || x1 > canvas.width || x2 < 0 || x2 > canvas.width) continue
        
        // 根据偏移程度设置颜色
        const offsetAbs = Math.abs(segment.offset)
        let color
        if (offsetAbs < 0.1) {
          color = 'rgba(34, 197, 94, 0.5)' // green
        } else if (offsetAbs < 0.3) {
          color = 'rgba(251, 146, 60, 0.5)' // orange
        } else {
          color = 'rgba(239, 68, 68, 0.5)' // red
        }
        
        // 绘制连线
        ctx.strokeStyle = color
        ctx.lineWidth = 2
        ctx.beginPath()
        ctx.moveTo(x1, y1)
        ctx.lineTo(x2, y2)
        ctx.stroke()
        
        // 绘制端点
        ctx.fillStyle = color
        ctx.beginPath()
        ctx.arc(x1, y1, 3.5, 0, 2 * Math.PI)
        ctx.fill()
        ctx.beginPath()
        ctx.arc(x2, y2, 3.5, 0, 2 * Math.PI)
        ctx.fill()
      }
    }

    // 延迟绘制，确保multitrack完全渲染
    const timer = setTimeout(() => {
      drawAlignmentLines()
    }, 300)

    // 当窗口大小改变时重绘
    const handleResize = () => {
      drawAlignmentLines()
    }
    window.addEventListener('resize', handleResize)

    return () => {
      clearTimeout(timer)
      window.removeEventListener('resize', handleResize)
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current)
      }
    }
  }, [canPlay, alignmentSegments, timeOffset, track0Duration, track1Duration])

  const handlePlayPause = () => {
    if (!multitrackRef.current || !canPlay) return

    if (isPlaying) {
      multitrackRef.current.pause()
    } else {
      multitrackRef.current.play()
    }
  }

  const handleStop = () => {
    if (!multitrackRef.current || !canPlay) return
    multitrackRef.current.setTime(0)
    multitrackRef.current.pause()
  }

  const handleMute = (newMuteState: 'both' | 'track0' | 'track1') => {
    if (!multitrackRef.current || !canPlay) return

    setMuteState(newMuteState)

    switch (newMuteState) {
      case 'track0':
        multitrackRef.current.setTrackVolume(0, 0)
        multitrackRef.current.setTrackVolume(1, 1)
        break
      case 'track1':
        multitrackRef.current.setTrackVolume(0, 1)
        multitrackRef.current.setTrackVolume(1, 0)
        break
      case 'both':
        multitrackRef.current.setTrackVolume(0, 1)
        multitrackRef.current.setTrackVolume(1, 1)
        break
    }
  }

  const formatTime = (time: number) => {
    const minutes = Math.floor(time / 60)
    const seconds = Math.floor(time % 60)
    return `${minutes}:${seconds.toString().padStart(2, '0')}`
  }

  if (!experimentAudioUrl || !testAudioUrl) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700">
        <h4 className="font-medium text-gray-900 dark:text-white mb-4">对齐音频播放器</h4>
        <div className="text-gray-500 dark:text-gray-400 text-center py-12 bg-gray-50 dark:bg-gray-700 rounded-lg">
          请上传测试音频
        </div>
      </div>
    )
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700">
      <div className="flex items-center justify-between mb-4">
        <h4 className="font-medium text-gray-900 dark:text-white">播放器</h4>
        {timeOffset !== 0 && (
          <div className="text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200 px-3 py-1 rounded-full">
            {timeOffset > 0 
              ? `实验音频延迟: +${timeOffset.toFixed(3)}s`
              : `测试音频延迟: +${Math.abs(timeOffset).toFixed(3)}s`
            }
          </div>
        )}
      </div>
      
      <div className="space-y-4">
        {/* Multitrack Container with Canvas Overlay */}
        <div className="bg-white dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600 overflow-hidden relative">
          <div 
            ref={containerRef}
            className="w-full"
            style={{ minHeight: '220px' }}
          />
          
          {/* Canvas Overlay for Alignment Lines */}
          <canvas
            ref={canvasRef}
            className="absolute top-0 left-0 w-full h-full pointer-events-none"
            style={{ zIndex: 10 }}
          />
          
          {isLoading && (
            <div className="absolute inset-0 bg-white dark:bg-gray-800 bg-opacity-90 flex items-center justify-center" style={{ zIndex: 20 }}>
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto mb-2" />
                <div className="text-gray-600 dark:text-gray-400">加载音频中...</div>
              </div>
            </div>
          )}
        </div>

        {/* Track Labels */}
        <div className="flex items-center justify-center space-x-6 text-xs">
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 rounded" style={{ backgroundColor: 'rgb(147, 51, 234)' }}></div>
            <span className="text-gray-600 dark:text-gray-400">{experimentTitle}</span>
            {muteState === 'track0' && <span className="text-red-600 font-bold">[静音]</span>}
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 rounded" style={{ backgroundColor: 'rgb(59, 130, 246)' }}></div>
            <span className="text-gray-600 dark:text-gray-400">{testTitle}</span>
            {muteState === 'track1' && <span className="text-red-600 font-bold">[静音]</span>}
          </div>
          {muteState === 'both' && (
            <div className="text-green-600 dark:text-green-400 font-bold">[双轨播放]</div>
          )}
        </div>

        {/* Controls */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            {/* Play/Pause */}
            <button
              onClick={handlePlayPause}
              disabled={!canPlay || isLoading}
              className={`flex items-center justify-center w-10 h-10 rounded-full text-white transition-colors
                ${isPlaying ? 'bg-red-600 hover:bg-red-700' : 'bg-primary-600 hover:bg-primary-700'}
                disabled:bg-gray-400 disabled:cursor-not-allowed`}
            >
              {isPlaying ? (
                <Pause className="h-5 w-5" />
              ) : (
                <Play className="h-5 w-5 ml-0.5" />
              )}
            </button>

            {/* Stop */}
            <button
              onClick={handleStop}
              disabled={!canPlay || isLoading}
              className="flex items-center justify-center w-10 h-10 rounded-full bg-gray-600 hover:bg-gray-700 
                disabled:bg-gray-400 disabled:cursor-not-allowed text-white transition-colors"
            >
              <RotateCcw className="h-5 w-5" />
            </button>

            {/* Time Display */}
            <div className="text-sm text-gray-600 dark:text-gray-400 font-mono">
              {formatTime(currentTime)} / {formatTime(duration)}
            </div>
          </div>

          {/* Mute Controls */}
          <div className="flex items-center space-x-2">
            <span className="text-xs text-gray-600 dark:text-gray-400 mr-2">音轨控制:</span>
            <button
              onClick={() => handleMute('both')}
              disabled={!canPlay}
              className={`px-3 py-1 text-xs rounded transition-colors ${
                muteState === 'both'
                  ? 'bg-green-600 text-white'
                  : 'bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-500'
              } disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              双轨
            </button>
            <button
              onClick={() => handleMute('track1')}
              disabled={!canPlay}
              className={`px-3 py-1 text-xs rounded transition-colors ${
                muteState === 'track1'
                  ? 'bg-purple-600 text-white'
                  : 'bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-500'
              } disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              仅实验
            </button>
            <button
              onClick={() => handleMute('track0')}
              disabled={!canPlay}
              className={`px-3 py-1 text-xs rounded transition-colors ${
                muteState === 'track0'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-500'
              } disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              仅测试
            </button>
          </div>
        </div>

        {/* Info */}
        <div className="text-xs text-gray-600 dark:text-gray-400">
          点击波形可跳转到指定位置。
          {timeOffset !== 0 && (
            <span className="text-blue-600 dark:text-blue-400 font-medium ml-1">
              {timeOffset > 0 
                ? '实验音频已根据第一个对齐点延迟播放。'
                : '测试音频已根据第一个对齐点延迟播放。'
              }
            </span>
          )}
          {matchStartTime !== undefined && matchEndTime !== undefined && (
            <span className="ml-2 text-green-600 dark:text-green-400">
              🎯 DTW匹配区域: {matchStartTime.toFixed(2)}s - {matchEndTime.toFixed(2)}s
            </span>
          )}
        </div>

        {/* Alignment Info (if available) */}
        {/* {alignmentSegments.length > 0 && (
          <div className="bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-700 dark:to-gray-600 rounded-lg p-4 border border-gray-200 dark:border-gray-600">
            <div className="text-xs text-gray-700 dark:text-gray-300 space-y-2">
              <div className="flex items-center justify-between">
                <span className="font-semibold">对齐可视化</span>
                <span className="text-gray-500 dark:text-gray-400">共 {alignmentSegments.length} 个对齐点</span>
              </div>
              <div className="flex items-center space-x-4 text-xs">
                <div className="flex items-center space-x-1">
                  <div className="w-3 h-3 rounded" style={{ backgroundColor: 'rgba(34, 197, 94, 0.6)' }}></div>
                  <span>精确对齐 (&lt;0.1s)</span>
                </div>
                <div className="flex items-center space-x-1">
                  <div className="w-3 h-3 rounded" style={{ backgroundColor: 'rgba(251, 146, 60, 0.6)' }}></div>
                  <span>适度偏移 (0.1-0.3s)</span>
                </div>
                <div className="flex items-center space-x-1">
                  <div className="w-3 h-3 rounded" style={{ backgroundColor: 'rgba(239, 68, 68, 0.6)' }}></div>
                  <span>较大偏移 (&gt;0.3s)</span>
                </div>
              </div>
              <div className="text-gray-500 dark:text-gray-400 text-xs">
                波形上的连线显示两个音频之间的时间对齐关系
              </div>
            </div>
          </div>
        )} */}
      </div>
    </div>
  )
}
